/**
 * ChatContext — the pop-out drawer's open/target state, so any page can open the ChatDock
 * drawer (e.g. PersonLane's "Chat with X's AI" button) without prop-drilling.
 *
 * The human identity ("who am I") is NO LONGER stored here — it lives in the single global
 * IdentityContext (the Navbar switcher) and is consumed wherever needed. We re-export the
 * people list for back-compat with older imports.
 */
import React, { createContext, useCallback, useContext, useState } from 'react';
import { IDENTITY_PEOPLE, PersonId } from './IdentityContext';

export type { PersonId };
// Back-compat alias: some components import CHAT_PEOPLE from here.
export const CHAT_PEOPLE: readonly PersonId[] = IDENTITY_PEOPLE;

interface ChatContextValue {
  /** Drawer open/close. */
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  /**
   * When set, the drawer should open-or-create a DIRECT conversation with this AI rep.
   * Consumed (and cleared) by ChatDock. Lets buttons elsewhere deep-link a 1:1 chat.
   */
  pendingDirectRep: string | null;
  requestDirectChat: (repRef: string) => void;
  clearPendingDirectRep: () => void;
  /**
   * When set, the drawer should open this exact conversation id. Used by the "Needs you"
   * grouping to jump straight into a waiting conversation.
   */
  pendingConversationId: number | null;
  requestOpenConversation: (id: number) => void;
  clearPendingConversation: () => void;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingDirectRep, setPendingDirectRep] = useState<string | null>(null);
  const [pendingConversationId, setPendingConversationId] = useState<number | null>(null);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen((v) => !v), []);

  const requestDirectChat = useCallback((repRef: string) => {
    setPendingDirectRep(repRef);
    setDrawerOpen(true);
  }, []);
  const clearPendingDirectRep = useCallback(() => setPendingDirectRep(null), []);

  const requestOpenConversation = useCallback((id: number) => {
    setPendingConversationId(id);
    setDrawerOpen(true);
  }, []);
  const clearPendingConversation = useCallback(() => setPendingConversationId(null), []);

  return (
    <ChatContext.Provider
      value={{
        drawerOpen,
        openDrawer,
        closeDrawer,
        toggleDrawer,
        pendingDirectRep,
        requestDirectChat,
        clearPendingDirectRep,
        pendingConversationId,
        requestOpenConversation,
        clearPendingConversation,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextValue => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within a ChatProvider');
  return ctx;
};
